import { useQuery } from "@tanstack/react-query";
import { userKeys } from "./user.keys";
import { useApi } from "../../api/ApiProvider";
import { AccountDto, LocationDto, UserDto, WorkPlanDto, WeeklyWorkDayDto } from "../../api-client";
import { useLocationId } from "./User.hooks";

export const useAccounts = (locationId?: string) => {
  const { user: userApi } = useApi();
  return useQuery<AccountDto[]>({
    queryKey: userKeys.accounts.detail(locationId!),
    queryFn: () => userApi.getAccountsByLocationId(locationId!),
    enabled: !!locationId,
    staleTime: 8 * 60 * 60 * 1000, // 8 hours
    meta: { errorMessage: "Konten konnten nicht geladen werden" },
  });
};

export const useAllUsersBasic = (withDeleted: boolean = false) => {
  const { user: userApi } = useApi();
  return useQuery<UserDto[]>({
    queryKey: userKeys.allUsersBasic.all(withDeleted),
    queryFn: () => userApi.getAllUsersBasic(undefined, undefined, undefined, withDeleted),
    staleTime: 60 * 60 * 1000, // 1 hour - users don't change frequently
    meta: { errorMessage: "Benutzer konnten nicht geladen werden" },
  });
};

/** Admin user list for one location (`""` = all locations the caller may see). */
export const useUsersByLocation = (locationId: string) => {
  const { user: userApi } = useApi();
  return useQuery<UserDto[]>({
    queryKey: userKeys.usersByLocation.detail(locationId),
    queryFn: () => userApi.getUsersBy(locationId),
    staleTime: 5 * 60 * 1000,
    meta: { errorMessage: "Benutzer konnten nicht geladen werden" },
  });
};

export const useUserByIdWithTherapies = (userId?: string, enabled: boolean = true) => {
  const { user: userApi } = useApi();

  return useQuery<UserDto | null>({
    queryKey: userId
      ? userKeys.userByIdWithTherapies.detail(userId)
      : [userKeys.userByIdWithTherapies.scope, { userId: undefined }],
    queryFn: async () => {
      if (!userId) return null;
      return userApi.getUserByIdWithTherapies(userId);
    },
    enabled: enabled && !!userId,
    staleTime: 60 * 60 * 1000, // 1 hour - users with therapies don't change frequently
    meta: { errorMessage: "Benutzer konnten nicht geladen werden" },
  });
};

export const useUserById = (userId?: string, enabled: boolean = true) => {
  const { user: userApi } = useApi();

  return useQuery<UserDto | null>({
    queryKey: userId
      ? userKeys.userById.detail(userId)
      : [userKeys.userById.scope, { userId: undefined }],
    queryFn: async () => {
      if (!userId) return null;
      return userApi.getUserById(userId);
    },
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Benutzer konnten nicht geladen werden" },
  });
};

export const useLocations = () => {
  const { user: userApi } = useApi();
  return useQuery<LocationDto[]>({
    queryKey: userKeys.locations.all(),
    queryFn: () => userApi.getAllLocations(),
    staleTime: 8 * 60 * 60 * 1000, // 8 hours
    meta: { errorMessage: "Standorte konnten nicht geladen werden" },
  });
};

export const useCurrentLocation = () => {
  const locationId = useLocationId();
  const { data: locations } = useLocations();
  // console.debug("useCurrentLocation locations:", locations, " locationId:", locationId);
  return locations?.find((location) => location.id === locationId);
};

/**
 * Query hook to fetch work plans for a user
 * Replaces UserStore.getWorkPlans
 */
export const useWorkPlans = (userId: string) => {
  const { user: userApi } = useApi();
  return useQuery<WorkPlanDto[]>({
    queryKey: userKeys.workPlans.detail(userId),
    queryFn: () => userApi.getWorkPlans(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Fehler beim Abrufen der Arbeitspläne" },
  });
};

/**
 * Query hook to fetch weekly work days by work plan
 * Replaces UserStore.getWeeklyWorkDaysByWorkPlan
 */
export const useWeeklyWorkDaysByWorkPlan = (userId: string, workPlanId?: number) => {
  const { user: userApi } = useApi();
  return useQuery<WeeklyWorkDayDto[]>({
    queryKey: userKeys.weeklyWorkDays.byWorkPlan(userId, workPlanId!),
    queryFn: () => userApi.getWeeklyWorkDaysByWorkPlan(userId, workPlanId!),
    enabled: !!userId && !!workPlanId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Fehler beim Abrufen der Arbeitszeiten" },
  });
};

/**
 * Query hook to fetch all weekly work days for a user
 * Replaces UserStore.getWeeklyWorkDays
 */
export const useWeeklyWorkDays = (userId: string) => {
  const { user: userApi } = useApi();
  return useQuery<WeeklyWorkDayDto[]>({
    queryKey: userKeys.weeklyWorkDays.byUser(userId),
    queryFn: () => userApi.getWeeklyWorkDays(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Fehler beim Abrufen der Arbeitszeiten" },
  });
};
